import { KNOWLEDGE_ARTICLES, KNOWLEDGE_DOMAINS, SECTION_LABELS, } from "./articles.js";
import { getKnowledgeDomain } from "./lookup.js";
const SEARCH_ALIASES = {
    ai: ["player ai", "coach"],
    assessment: ["lama", "maturity"],
    coach: ["coaching", "player ai"],
    facilitate: ["facilitator", "workshop"],
    facilitation: ["facilitator", "workshop"],
    hoshin: ["strategy deployment"],
    lama: ["maturity assessment"],
    rca: ["root cause"],
    vsm: ["value stream mapping", "value stream"],
};
/** Escape literal user text before inserting it into a search pattern. */
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}
/** Match literal alternatives at Unicode word boundaries; optionally find every occurrence. */
export function knowledgeTermPattern(terms, global = false) {
    const alternatives = [...terms]
        .filter(Boolean)
        .sort((left, right) => right.length - left.length)
        .map(escapeRegExp)
        .join("|");
    if (!alternatives)
        return new RegExp("(?!)", global ? "giu" : "iu");
    return new RegExp(`(?<![\\p{Letter}\\p{Number}\\p{Mark}])(${alternatives})(?![\\p{Letter}\\p{Number}\\p{Mark}])`, `${global ? "g" : ""}iu`);
}
/** Test one literal term using the shared Unicode word-boundary rules. */
export function matchesKnowledgeTerm(value, term) {
    return knowledgeTermPattern([term]).test(value);
}
/** Lowercase optional catalogue text consistently for scoring and excerpts. */
function normalise(value) {
    return value?.toLocaleLowerCase() ?? "";
}
/** Expand whitespace-separated terms using only explicitly registered aliases. */
function searchConcepts(query) {
    return normalise(query)
        .trim()
        .split(/\s+/u)
        .filter(Boolean)
        .map((term) => [
        term,
        ...(Object.hasOwn(SEARCH_ALIASES, term) ? SEARCH_ALIASES[term] : []),
    ]);
}
/** Sum each concept's strongest field weight; reject a guide at its first missing concept. */
function articleSearchScore(article, concepts) {
    const domain = getKnowledgeDomain(article.domainId);
    const fields = [
        { value: normalise(article.title), weight: 12 },
        { value: normalise(domain?.name), weight: 10 },
        { value: normalise(domain?.kicker), weight: 7 },
        { value: normalise(SECTION_LABELS[article.section]), weight: 7 },
        { value: normalise(article.summary), weight: 5 },
        { value: normalise(article.slug), weight: 3 },
        { value: normalise(article.body), weight: 1 },
    ];
    let total = 0;
    for (const alternatives of concepts) {
        let best = 0;
        for (const term of alternatives) {
            const pattern = knowledgeTermPattern([term]);
            for (const field of fields) {
                if (field.weight > best && pattern.test(field.value))
                    best = field.weight;
            }
        }
        if (best === 0)
            return null;
        total += best;
    }
    return total;
}
/** Relevance used by both browser search and bounded server guide search. */
export function knowledgeArticleSearchScore(article, query) {
    return articleSearchScore(article, searchConcepts(query));
}
/** Remove Markdown presentation syntax before selecting a readable search excerpt. */
function plainText(markdown) {
    return markdown
        .replace(/^\|[|:\t -]+\|[ \t]*$/gmu, "")
        .replace(/^#{1,6}\s+/gmu, "")
        .replace(/\[(.*?)\]\([^)]*\)/gu, "$1")
        .replace(/[*_`>|]/gu, "")
        .replace(/\s+/gu, " ")
        .trim();
}
/** Return a bounded excerpt and literal highlight terms around the earliest body match. */
function excerptFor(article, concepts) {
    if (concepts.length === 0) {
        return { excerpt: article.summary, highlightTerms: [] };
    }
    const body = plainText(article.body);
    const alternatives = [...new Set(concepts.flat())].sort((left, right) => right.length - left.length);
    const bodyTerms = alternatives.filter((term) => matchesKnowledgeTerm(body, term));
    const visibleTerms = alternatives.filter((term) => matchesKnowledgeTerm(`${article.title} ${article.summary} ${body}`, term));
    if (bodyTerms.length === 0) {
        return {
            excerpt: article.summary,
            highlightTerms: visibleTerms,
        };
    }
    const firstMatch = Math.min(...bodyTerms.map((term) => normalise(body).search(knowledgeTermPattern([term]))));
    const rawStart = Math.max(0, firstMatch - 72);
    const rawEnd = Math.min(body.length, firstMatch + 150);
    const startSpace = body.indexOf(" ", rawStart);
    const start = rawStart === 0 || startSpace < 0 ? 0 : startSpace;
    const endSpace = body.lastIndexOf(" ", rawEnd);
    const end = rawEnd === body.length || endSpace <= start ? rawEnd : endSpace;
    return {
        excerpt: `${start > 0 ? "…" : ""}${body.slice(start, end).trim()}${end < body.length ? "…" : ""}`,
        highlightTerms: visibleTerms,
    };
}
/** Search the supplied catalogue with all-concept matching and stable relevance order. */
export function searchKnowledgeArticles(query, filters = {}, articles = KNOWLEDGE_ARTICLES) {
    const concepts = searchConcepts(query);
    const candidates = articles.filter((article) => (!filters.domainId || article.domainId === filters.domainId) &&
        (!filters.section || article.section === filters.section));
    if (concepts.length === 0) {
        const suggestions = filters.domainId || filters.section
            ? candidates
            : KNOWLEDGE_DOMAINS.flatMap((domain) => {
                const firstArticle = candidates.find((article) => article.domainId === domain.id);
                return firstArticle ? [firstArticle] : [];
            });
        return suggestions.map((article) => ({
            article,
            ...excerptFor(article, []),
        }));
    }
    return candidates
        .map((article) => ({
        article,
        score: articleSearchScore(article, concepts),
    }))
        .filter((result) => result.score !== null)
        .sort((left, right) => right.score - left.score ||
        left.article.domainId.localeCompare(right.article.domainId) ||
        left.article.order - right.article.order ||
        left.article.id.localeCompare(right.article.id))
        .map(({ article }) => ({ article, ...excerptFor(article, concepts) }));
}
/** Return only the ordered articles from the shared Library search results. */
export function findKnowledgeArticles(query, filters = {}, articles = KNOWLEDGE_ARTICLES) {
    return searchKnowledgeArticles(query, filters, articles).map(({ article }) => article);
}
//# sourceMappingURL=search.js.map